// services/VerificadorImpresorasService.js

/**
 * Servicio optimizado para verificar impresoras
 * - Completamente desacoplado de productos/platillos
 * - Depende exclusivamente de API.impresoras.getImpresorasNecesarias
 * - Funciona con caché de 5 minutos para minimizar impacto en rendimiento
 */
class VerificadorImpresorasService {
  constructor() {
    // Referencia al electron IPC (con validación para evitar errores)
    this.ipcRenderer = (window.require && typeof window.require === 'function')
      ? window.require('electron').ipcRenderer
      : null;
    this.ipcRenderer = window.electron ? window.electron.ipcRenderer : null;
    // Referencia al servicio API (debe ser inyectado)
    this.api = null;

    // Cache de estado de impresoras
    this.estadoImpresoras = {};
    this.ultimaVerificacion = null;

    // Tiempo máximo de cache en ms (5 minutos)
    this.tiempoCacheMax = 5 * 60 * 1000;

    // Control de verificación para evitar bucles
    this.verificacionEnProceso = false;

    // Intervalo de actualización automática
    this.intervaloActualizacion = null;

    // NO iniciar verificación automática en el constructor
    console.log("Verificador de impresoras inicializado en modo pasivo (solo caché)");
  }

  // Método para iniciar verificación bajo demanda
  iniciarVerificacionManual() {
    if (!this.intervaloActualizacion && this.ipcRenderer) {
      console.log("Iniciando verificación manual de impresoras");
      this.iniciarVerificacionPeriodica();
    }
  }

  /**
   * Establece la referencia al servicio API
   * @param {Object} apiService - Referencia al servicio API
   */
  setApiService(apiService) {
    // Validar que el API service sea un objeto
    if (apiService && typeof apiService === 'object') {
      this.api = apiService;
      console.log("API Service configurado en VerificadorImpresorasService");
    } else {
      console.warn("API Service inválido proporcionado a VerificadorImpresorasService");
    }
  }

  /**
   * Inicia la verificación periódica de impresoras cada 5 minutos
   */
  iniciarVerificacionPeriodica() {
    // Verificar que tenemos acceso al IPC
    if (!this.ipcRenderer) {
      console.warn("No se puede iniciar verificación periódica sin IPC Renderer");
      return;
    }

    // Limpiar intervalo existente si hay uno
    if (this.intervaloActualizacion) {
      clearInterval(this.intervaloActualizacion);
    }

    // Crear nuevo intervalo
    this.intervaloActualizacion = setInterval(() => {
      if (!this.verificacionEnProceso) {
        this.verificacionEnProceso = true;
        this.refrescarEstadoImpresoras()
          .catch(error => {
            console.error("Error en verificación periódica de impresoras:", error);
          })
          .finally(() => {
            this.verificacionEnProceso = false;
          });
      }
    }, this.tiempoCacheMax);

    // Hacer una verificación inicial
    if (!this.verificacionEnProceso) {
      this.verificacionEnProceso = true;
      this.refrescarEstadoImpresoras()
        .catch(error => {
          console.error("Error en verificación inicial de impresoras:", error);
        })
        .finally(() => {
          this.verificacionEnProceso = false;
        });
    }
  }

  /**
   * Detiene la verificación periódica
   */
  detenerVerificacionPeriodica() {
    if (this.intervaloActualizacion) {
      clearInterval(this.intervaloActualizacion);
      this.intervaloActualizacion = null;
    }
  }

  /**
  * Método que elimina COMPLETAMENTE la dependencia de productos o platillos
  * @param {Object} platillo - (Ignorado) No se usa para determinar la impresora
  * @returns {string|null} - Siempre null para forzar uso de impresoras predeterminadas
  */
  getImpresoraPorPlatillo(platillo) {
    // NO USAR el platillo para obtener impresora
    // Forzar el uso del endpoint dedicado en lugar de asociar impresoras a platillos
    return null;
  }

  /**
   * Verifica el estado de una lista de impresoras
   * @param {Array} impresoras - Lista de nombres de impresoras
   * @returns {Promise<Array>} - Resultados de la verificación
   */
  async verificarImpresoras(impresoras) {
    // Validar que impresoras sea un array
    if (!impresoras || !Array.isArray(impresoras)) {
      console.warn("Lista de impresoras inválida proporcionada a verificarImpresoras");
      return [];
    }

    // Si no hay impresoras para verificar, devolver array vacío
    if (impresoras.length === 0) {
      return [];
    }

    // Si no tenemos IPC, usar simulación
    if (!this.ipcRenderer) {
      console.warn("Electron IPC no disponible para verificar impresoras");
      return impresoras.map(imp => ({
        impresora: imp,
        disponible: true, // Asumimos disponible para evitar bloqueos
        online: true,
        error: "IPC no disponible para verificación real",
        detalles: null
      }));
    }

    // Obtener la hora actual
    const ahora = Date.now();

    // Determinar si debemos refrescar el caché
    const debeRefrescar = !this.ultimaVerificacion ||
      (ahora - this.ultimaVerificacion > this.tiempoCacheMax);

    // Si debemos refrescar, obtener todas las impresoras del sistema
    if (debeRefrescar) {
      try {
        await this.refrescarEstadoImpresoras();
      } catch (error) {
        console.error("Error al refrescar estado de impresoras:", error);
        // Continuar con el caché existente
      }
    }

    // Verificar cada impresora solicitada
    const resultados = await Promise.all(impresoras.map(async (nombreImpresora) => {
      // Validar que nombreImpresora sea un string no vacío
      if (!nombreImpresora || typeof nombreImpresora !== 'string') {
        return {
          impresora: String(nombreImpresora || 'Desconocida'),
          disponible: false,
          online: false,
          error: "Nombre de impresora inválido",
          detalles: null
        };
      }

      // Si tenemos el estado en caché y no necesitamos refrescar
      if (this.estadoImpresoras[nombreImpresora] && !debeRefrescar) {
        return {
          impresora: nombreImpresora,
          disponible: this.estadoImpresoras[nombreImpresora].disponible || false,
          online: this.estadoImpresoras[nombreImpresora].online || false,
          error: this.estadoImpresoras[nombreImpresora].error || null,
          detalles: this.estadoImpresoras[nombreImpresora]
        };
      }

      // Si no está en caché o necesitamos refrescar
      try {
        const estadoImpresora = await this.ipcRenderer.invoke(
          'get-printer-status',
          nombreImpresora
        );

        // Validar respuesta
        if (!estadoImpresora || typeof estadoImpresora !== 'object') {
          throw new Error(`Respuesta inválida para impresora ${nombreImpresora}`);
        }

        // Actualizar caché
        this.estadoImpresoras[nombreImpresora] = {
          disponible: estadoImpresora.online || false,
          online: estadoImpresora.online || false,
          error: estadoImpresora.error || null,
          mensaje: estadoImpresora.mensaje || "Sin información",
          ultimaVerificacion: ahora
        };

        return {
          impresora: nombreImpresora,
          disponible: estadoImpresora.online || false,
          online: estadoImpresora.online || false,
          error: estadoImpresora.error || null,
          detalles: estadoImpresora
        };
      } catch (error) {
        console.error(`Error al verificar impresora ${nombreImpresora}:`, error);

        // Actualizar caché con el error
        this.estadoImpresoras[nombreImpresora] = {
          disponible: false,
          online: false,
          error: `Error de comunicación: ${error.message || 'Desconocido'}`,
          mensaje: "No se pudo obtener el estado de la impresora",
          ultimaVerificacion: ahora
        };

        return {
          impresora: nombreImpresora,
          disponible: false,
          online: false,
          error: `Error de comunicación: ${error.message || 'Desconocido'}`,
          detalles: null
        };
      }
    }));

    return resultados;
  }

  /**
   * Refresca el estado de todas las impresoras del sistema
   * Versión mejorada con manejo de errores
   * @returns {Promise<Object>}
   */
  async refrescarEstadoImpresoras() {
    if (!this.ipcRenderer) {
      console.warn("Electron IPC no disponible para refrescarEstadoImpresoras");
      return this.estadoImpresoras || {};
    }

    try {
      // Limitar la frecuencia de verificación
      const ahora = Date.now();
      if (this.ultimaVerificacion && (ahora - this.ultimaVerificacion < this.tiempoCacheMax)) {
        console.log("Usando caché de estado de impresoras (última verificación hace",
          Math.floor((ahora - this.ultimaVerificacion) / 1000), "segundos)");
        return this.estadoImpresoras;
      }

      console.log("Iniciando verificación de impresoras...");

      // Obtener la lista de impresoras del sistema (usando caché del sistema)
      let impresorasDisponibles = [];
      try {
        // Usar forceRefresh=false para usar la caché si no ha expirado
        impresorasDisponibles = await this.ipcRenderer.invoke('get-all-printers', false);
      } catch (error) {
        console.error("Error obteniendo impresoras:", error);
        // Continuar con un array vacío o la última lista conocida
        impresorasDisponibles = [];
      }

      // Verificar que impresorasDisponibles sea un array
      if (!Array.isArray(impresorasDisponibles)) {
        console.warn("La lista de impresoras no es un array:", impresorasDisponibles);
        impresorasDisponibles = [];
      }

      // Actualizar última verificación
      this.ultimaVerificacion = ahora;

      // Crear un nuevo objeto de estado
      const nuevoEstado = {};

      // Verificar cada impresora obtenida
      for (const impresora of impresorasDisponibles) {
        try {
          // Verificar que el objeto impresora sea válido
          if (!impresora || typeof impresora !== 'object' || !impresora.name) {
            continue;
          }

          // Evitar verificaciones redundantes con un tiempo mínimo entre verificaciones
          if (this.estadoImpresoras[impresora.name] &&
            this.estadoImpresoras[impresora.name].ultimaVerificacion &&
            (ahora - this.estadoImpresoras[impresora.name].ultimaVerificacion < this.tiempoCacheMax)) {
            // Usar caché existente
            nuevoEstado[impresora.name] = this.estadoImpresoras[impresora.name];
            continue;
          }

          // Usar estado básico basado en los datos que ya tenemos
          // Sin hacer llamadas adicionales para verificar cada impresora
          nuevoEstado[impresora.name] = {
            disponible: true, // Asumir disponible para no bloquear la interfaz
            online: true,
            error: null,
            mensaje: "Estado basado en caché del sistema",
            detalles: { ...impresora },
            ultimaVerificacion: this.ultimaVerificacion
          };
        } catch (error) {
          console.error(`Error al procesar impresora ${impresora?.name || 'desconocida'}:`, error);
        }
      }

      // Actualizar el estado completo
      this.estadoImpresoras = nuevoEstado;

      return nuevoEstado;
    } catch (error) {
      console.error("Error general al refrescar estado de impresoras:", error);
      return this.estadoImpresoras || {};
    }
  }

  /**
   * Método para verificar impresoras independientemente de productos
   * @returns {Promise<Object>} Estado actualizado de impresoras
   */
  /**
   * Obtiene la impresora predeterminada del sistema
   * @returns {Promise<string|null>} Nombre de la impresora predeterminada o null
   */
  async obtenerImpresoraPredeterminada() {
    try {
      if (!this.ipcRenderer) {
        return null;
      }

      // Obtener todas las impresoras del sistema
      const impresoras = await this.ipcRenderer.invoke('get-all-printers', false);

      // Buscar la impresora predeterminada
      const impresoraPredeterminada = Array.isArray(impresoras)
        ? impresoras.find(imp => imp && imp.isDefault)
        : null;

      return impresoraPredeterminada ? impresoraPredeterminada.name : null;
    } catch (error) {
      console.error("Error al obtener impresora predeterminada:", error);
      return null;
    }
  }
  async obtenerTodasLasImpresorasUtilizadas() {
    try {
      // Conjunto para almacenar impresoras únicas
      const impresoras = new Set();

      // SOLAMENTE consultar la API para impresoras necesarias (sin considerar productos)
      if (this.api) {
        try {
          if (typeof this.api.impresoras?.getImpresorasNecesarias === 'function') {
            // No forzar actualización para usar la caché existente
            const impresorasNecesarias = await this.api.impresoras.getImpresorasNecesarias(false);

            if (Array.isArray(impresorasNecesarias)) {
              impresorasNecesarias.forEach(impresora => {
                if (!impresora) return;

                // Normalizar el formato de la impresora
                const nombre = typeof impresora === 'string' ?
                  impresora :
                  (impresora.alias || impresora.nombre);

                if (nombre) {
                  impresoras.add(nombre.toUpperCase());
                }
              });
            }
          }
        } catch (err) {
          console.error("Error al obtener impresoras necesarias:", err);
        }
      }

      // Si no hay impresoras, usar un array vacío para evitar verificaciones innecesarias
      // Esto evita que intente verificar impresoras predeterminadas innecesariamente
      return Array.from(impresoras);
    } catch (error) {
      console.error("Error al obtener todas las impresoras utilizadas:", error);
      return []; // Devolver array vacío para evitar verificaciones adicionales
    }
  }
  /**
   * Método especial para forzar verificación completa
   * Usado principalmente desde el botón de verificar
   * Solo depende de API.impresoras.getImpresorasNecesarias
   * @returns {Promise<Object>} Estado actualizado de impresoras
   */
  async forzarVerificacionImpresoras() {
    try {
      console.log("Forzando verificación de impresoras desde botón");

      if (!this.ipcRenderer) {
        console.warn("Electron IPC no disponible para verificación forzada");
        return this.estadoImpresoras || {};
      }

      // Evitar verificaciones simultáneas
      if (this.verificacionEnProceso) {
        console.log("Ya hay una verificación en curso");
        return this.estadoImpresoras || {};
      }

      this.verificacionEnProceso = true;

      try {
        // ÚNICAMENTE obtener impresoras necesarias desde el endpoint API
        // (No considerar productos en absoluto)
        let impresorasNecesarias = [];

        if (this.api && typeof this.api.impresoras?.getImpresorasNecesarias === 'function') {
          impresorasNecesarias = await this.api.impresoras.getImpresorasNecesarias(true);
        }

        const ahora = Date.now();
        const nuevoEstado = {};

        // Convertir a array de nombres para verificación
        const nombresImpresoras = Array.isArray(impresorasNecesarias) ?
          impresorasNecesarias.map(imp => {
            if (typeof imp === 'string') return imp;
            return imp?.alias || imp?.nombre || null;
          }).filter(Boolean) : [];

        // Si no hay impresoras para verificar, usar al menos una predeterminada
        if (nombresImpresoras.length === 0) {
          const predeterminada = await this.obtenerImpresoraPredeterminada();
          if (predeterminada) {
            nombresImpresoras.push(predeterminada);
          } else {
            nombresImpresoras.push("DEFAULT");
          }
        }

        // Verificar cada impresora individualmente
        console.log(`Verificando ${nombresImpresoras.length} impresoras...`);

        for (const nombreImpresora of nombresImpresoras) {
          try {
            if (!nombreImpresora) continue;

            const estadoImpresora = await this.ipcRenderer.invoke('get-printer-status', nombreImpresora);

            nuevoEstado[nombreImpresora] = {
              disponible: estadoImpresora.online || false,
              online: estadoImpresora.online || false,
              error: estadoImpresora.error || null,
              mensaje: estadoImpresora.mensaje || "Verificación manual completada",
              detalles: { ...(estadoImpresora.detalles || {}) },
              ultimaVerificacion: ahora
            };
          } catch (error) {
            console.error(`Error al verificar impresora ${nombreImpresora}:`, error);

            nuevoEstado[nombreImpresora] = {
              disponible: false,
              online: false,
              error: `Error: ${error.message || 'Desconocido'}`,
              mensaje: "Error en verificación manual",
              ultimaVerificacion: ahora
            };
          }
        }

        // Actualizar el estado completo
        this.ultimaVerificacion = ahora;
        this.estadoImpresoras = nuevoEstado;

        return nuevoEstado;
      } finally {
        this.verificacionEnProceso = false;
      }
    } catch {

    }
  }
}
const verificadorImpresorasInstance = new VerificadorImpresorasService();

// CORRECCIÓN: Exportar la instancia como default
export default verificadorImpresorasInstance;